import ActionMenu from 'features/ActionMenu';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import GridFormField from 'shared/components/GridFormField';
import IconButton from '@material-ui/core/IconButton';
import Modal from '@material-ui/core/Modal';
import Paper from '@material-ui/core/Paper';
import React from 'react';
import removeHTML from 'shared/utils/removeHTML';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { InputAdornment } from '@material-ui/core/Input';
import { MANDATORY, OPTIONAL } from 'redux/domain/Settings/constants';
import {
	formatSsn,
	formatSsnHidden,
	normalizeSsn,
	normalizeSsnHidden,
} from 'shared/utils/formatting';
import { exactStrLength, maxNameLength, maxStrLength, required } from 'shared/utils/validations';
import { PropTypes } from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import withRouter from 'shared/utils/WithRouter';

export const styles = {
	footer: {
		paddingTop: '20px',
	},
	grid: {
		flexGrow: 1,
	},
	gridDiv: {
		width: '100%',
	},
	message: {
		padding: '15px 0',
	},
	modal: {
		position: 'fixed',
		width: '100%',
		maxWidth: '400px',
		height: '100%',
		maxHeight: '250px',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		padding: '20px',
		'@media only screen and (max-width: 420px) and (orientation: portrait)': {
			maxWidth: '275px',
		},
	},
	modalButtons: {
		position: 'absolute',
		bottom: '20px',
		right: '20px',
	},
	paper: {
		width: '95%',
		maxWidth: '800px',
		height: 'auto',
		margin: 'auto',
	},
	paperDiv: {
		padding: '25px',
		marginBottom: '15px',
	},
	toolbar: {
		display: 'flex',
		flexFlow: 'row wrap',
		justifyContent: 'space-between',
		position: 'sticky',
		top: '0',
		backgroundColor: '#FFFFFF',
		marginBottom: '8px',
		zIndex: '2',
		fallbacks: {
			position: '-webkit-sticky',
		},
	},
};

class FindCustomer extends React.Component {
	constructor() {
		super();
		this.state = { showSsn: false };
		this.handleClickShowSsn = this.handleClickShowSsn.bind(this);
	}

	componentDidMount() {
		const { history, settings } = this.props;
		if (settings === undefined) {
			props.router.navigate('/now');
		}
		window.scrollTo(0, 0);
	}

	handleClickShowSsn() {
		this.setState({ showSsn: !this.state.showSsn });
	}

	render() {
		const {
			classes,
			closeModal,
			handleSubmit,
			modalOpen,
			modalMessage,
			openCancelConfirmModal,
			reset,
			settings,
			valid,
		} = this.props;

		return settings != null ? (
			<form onSubmit={handleSubmit} autoComplete="off">
				<Toolbar className={classes.toolbar}>
					<ActionMenu />
					<div>
						<Button onClick={openCancelConfirmModal}>Cancel</Button>
						<Button variant="contained" disabled={!valid} color="primary" type="submit">
							Continue
						</Button>
					</div>
				</Toolbar>
				<Paper elevation={6} className={classes.paper}>
					<div className={classes.paperDiv}>
						<Typography type="headline" gutterBottom>
							{settings.generalSettings.PersonalInformationTitle}
						</Typography>
						<Typography type="subheading" component="div" className={classes.message}>
							<div
								// eslint-disable-next-line react/no-danger
								dangerouslySetInnerHTML={{
									__html: removeHTML(settings.generalSettings.PersonalInformationText),
								}}
							/>
						</Typography>
						<div className={classes.gridDiv}>
							<Grid className={classes.grid} container spacing={10}>
								<GridFormField
									fieldSettings={{ fieldStatus: MANDATORY }}
									placeholder={settings.fieldAndFooterSettings.FirstNameLabel}
									description={settings.fieldAndFooterSettings.FirstNameDescription}
									name="firstName"
									validation={[required, maxStrLength(30), maxNameLength]}
								/>
								<GridFormField
									fieldSettings={{ fieldStatus: MANDATORY }}
									placeholder={settings.fieldAndFooterSettings.LastNameLabel}
									description={settings.fieldAndFooterSettings.LastNameDescription}
									name="lastName"
									validation={[required, maxStrLength(30), maxNameLength]}
								/>
								<GridFormField
									fieldSettings={{ fieldStatus: MANDATORY }}
									placeholder={settings.fieldAndFooterSettings.Address1Label}
									description={settings.fieldAndFooterSettings.Address1Description}
									name="address1"
									validation={[required, maxStrLength(30)]}
								/>
								<GridFormField
									fieldSettings={{ fieldStatus: OPTIONAL }}
									placeholder={settings.fieldAndFooterSettings.Address2Label}
									description={settings.fieldAndFooterSettings.Address2Description}
									name="address2"
									validation={maxStrLength(30)}
								/>
								<GridFormField
									fieldSettings={{ fieldStatus: MANDATORY }}
									placeholder={settings.fieldAndFooterSettings.PostalLabel}
									description={settings.fieldAndFooterSettings.PostalDescription}
									name="zipCode"
									validation={[required, maxStrLength(5)]}
								/>
								<GridFormField
									fieldSettings={{ fieldStatus: MANDATORY }}
									placeholder={settings.fieldAndFooterSettings.SsnLabel}
									description={settings.fieldAndFooterSettings.SsnDescription}
									name="ssn"
									validation={[required, exactStrLength(9)]}
									format={this.state.showSsn ? formatSsn : formatSsnHidden}
									normalize={this.state.showSsn ? normalizeSsn : normalizeSsnHidden}
									endAdornment={
										<InputAdornment position="end">
											<IconButton
												onClick={this.handleClickShowSsn}
												onMouseDown={e => e.preventDefault()} // Don't change focused field when pressing button
											>
												{this.state.showSsn ? <VisibilityOff /> : <Visibility />}
											</IconButton>
										</InputAdornment>
									}
								/>
							</Grid>
						</div>
						<Typography type="caption" component="div" className={classes.footer}>
							<div
								// eslint-disable-next-line react/no-danger
								dangerouslySetInnerHTML={{
									__html: removeHTML(settings.fieldAndFooterSettings.InitialEntryFooter),
								}}
							/>
						</Typography>
					</div>
				</Paper>
				<Modal open={modalOpen}>
					<Paper className={classes.modal}>
						{/* Note: in MaterialUI beta 32, 'type' is changed to 'variant' */}
						<Typography type="headline" gutterBottom>
							Error
						</Typography>
						<Typography type="subheading">{modalMessage}</Typography>
						<div className={classes.modalButtons}>
							<Button
								onClick={() => {
									reset();
									closeModal();
								}}
							>
								Clear
							</Button>
							<Button onClick={closeModal}>Fix</Button>
						</div>
					</Paper>
				</Modal>
			</form>
		) : null;
	}
}

FindCustomer.propTypes = {
	classes: PropTypes.shape({}).isRequired,
	closeModal: PropTypes.func.isRequired,
	handleSubmit: PropTypes.func.isRequired,
	modalOpen: PropTypes.bool.isRequired,
	modalMessage: PropTypes.string.isRequired,
	openCancelConfirmModal: PropTypes.func.isRequired,
	reset: PropTypes.func.isRequired,
	settings: PropTypes.shape({
		primary: PropTypes.objectOf(
			PropTypes.oneOfType([
				PropTypes.shape({
					fieldId: PropTypes.string,
					fieldStatus: PropTypes.string,
					fieldSubHeaderType: PropTypes.string,
				}),
				PropTypes.number,
			]),
		),
		coApp: PropTypes.objectOf(
			PropTypes.oneOfType([
				PropTypes.shape({
					fieldId: PropTypes.string,
					fieldStatus: PropTypes.string,
					fieldSubHeaderType: PropTypes.string,
				}),
				PropTypes.number,
			]),
		),
		comboFields: PropTypes.objectOf(
			PropTypes.oneOfType([
				PropTypes.arrayOf(
					PropTypes.shape({ displayText: PropTypes.string, value: PropTypes.string }),
				),
				PropTypes.number,
			]),
		),
		comboFieldsByValue: PropTypes.objectOf(
			PropTypes.oneOfType([
				PropTypes.objectOf(
					PropTypes.shape({
						displayOrder: PropTypes.number,
						fieldName: PropTypes.string,
						displayText: PropTypes.string,
						returnValue: PropTypes.string,
						requiresEmploymentData: PropTypes.bool,
					}),
				),
				PropTypes.number,
			]),
		),
		generalSettings: PropTypes.shape({}),
	}),
	valid: PropTypes.bool.isRequired,
};

FindCustomer.defaultProps = {
	settings: undefined,
};

export default withRouter(withStyles(styles, { name: 'FindCustomer' })(FindCustomer));
